import {
  FIELD_TYPES,
  ITEM_SORTS,
  ITEM_CONDITIONS,
  ITEM_STATUSES,
  MAX_FIELDS_PER_ITEM,
  MAX_FIELD_LABEL_LENGTH,
  MAX_FIELD_VALUE_LENGTH,
  MAX_QUANTITY,
  MAX_TAGS_PER_ITEM,
  type FieldInput,
  type FieldType,
  type FieldVisibility,
  type ItemSort,
  type ItemCondition,
  type ItemStatus,
} from "./types.js";

/**
 * Input validation shared by the worker and the web app, so the browser can
 * refuse something before a round trip and the worker can refuse it again
 * afterwards. The worker's copy is the one that counts.
 */

/** Addresses are compared and stored lowercased and trimmed, everywhere. */
export const normaliseEmail = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/**
 * Deliberately not RFC 5322. That grammar accepts things no mail server will,
 * and the useful question here is "could this plausibly be typed by someone who
 * meant an address" -- anything that clears this and is still wrong bounces,
 * which is feedback this app does not need to duplicate.
 */
const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

export const MAX_EMAIL_LENGTH = 320;

export function validEmail(value: unknown): boolean {
  const email = normaliseEmail(value);
  return email.length > 0 && email.length <= MAX_EMAIL_LENGTH && EMAIL.test(email);
}

/** Free text an admin types about an allowlist entry. Length-capped, not sanitised: it renders as text. */
export const MAX_NOTE_LENGTH = 200;

export const clampNote = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .slice(0, MAX_NOTE_LENGTH);

/** What somebody calls a passkey ("My laptop"). Optional -- an empty one just shows its date instead. */
export const MAX_PASSKEY_NAME_LENGTH = 60;

export const clampPasskeyName = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .slice(0, MAX_PASSKEY_NAME_LENGTH);

/**
 * Ids are minted server-side and are opaque to the client, but they travel in
 * URLs, so anything that is not this shape is refused before it reaches a
 * query or a Durable Object name.
 */
const ID = /^[a-z0-9]{8,32}$/;

export const validId = (value: unknown): value is string => typeof value === "string" && ID.test(value);

/* ------------------------------------------------------------- item text */

/**
 * Caps, not rules. Nothing here is required -- an item saved from three photos
 * and nothing else is the whole point of Stage 1 -- so validation's only job is
 * to stop something absurd reaching a row, and every field clamps rather than
 * refusing. A capture flow that rejects a paste is a capture flow somebody
 * stops using.
 */
export const MAX_TITLE_LENGTH = 140;
export const MAX_TEXT_LENGTH = 4000;
export const MAX_NAME_LENGTH = 80;

export const clampText = (value: unknown, max: number): string =>
  String(value ?? "")
    .trim()
    .slice(0, max);

export const clampTitle = (value: unknown): string => clampText(value, MAX_TITLE_LENGTH);

/** A location or category name. Whitespace collapses, because "Shelf  A" and "Shelf A" are one shelf. */
export const clampName = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, MAX_NAME_LENGTH);

/* ----------------------------------------------------------------- money */

/** A million dollars, in cents. Not a judgement about the basement; a bound on a typo. */
export const MAX_PRICE_CENTS = 100_000_000;

/**
 * Money is an integer number of cents or it is nothing. A float would be a
 * rounding bug waiting for Stage 4 to print it on a listing, so nothing in this
 * app ever holds a price as one.
 */
export function normalisePriceCents(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const cents = Math.round(Number(value));
  if (!Number.isFinite(cents) || cents < 0 || cents > MAX_PRICE_CENTS) return null;
  return cents;
}

/**
 * What somebody types into a price box, as cents. "$12.50", "12.5" and "12"
 * all mean the same thing; anything else means null, which is also what an
 * empty box means -- the field is optional and a half-typed price is not an
 * error worth interrupting a capture for.
 */
export function centsFromInput(value: string): number | null {
  const cleaned = value.replace(/[^0-9.]/g, "");
  if (cleaned === "") return null;
  const amount = Number(cleaned);
  if (!Number.isFinite(amount)) return null;
  return normalisePriceCents(Math.round(amount * 100));
}

/**
 * Cents back to what goes in a price box: "1234.50", no symbol, no commas, so
 * it reads back through `centsFromInput` unchanged. For a box's value and for
 * files (CSV, a listing); a screen showing an amount uses `formatMoney`.
 */
export function priceFromCents(cents: number | null): string {
  if (cents === null) return "";
  return (cents / 100).toFixed(2);
}

/**
 * The currencies an inventory can be kept in, by ISO code. Every amount is
 * stored as bare hundredths, so only currencies written with two decimals are
 * here: a yen price typed as "1500" would be stored and shown as 1,500.00,
 * which no Japanese price reads like. Changing an inventory's currency
 * relabels its amounts; nothing is converted (see docs/notes/decisions.md).
 */
export const CURRENCIES = [
  "USD",
  "CAD",
  "EUR",
  "GBP",
  "AUD",
  "NZD",
  "CHF",
  "SEK",
  "NOK",
  "DKK",
  "MXN",
  "INR",
  "ZAR",
  "SGD",
  "HKD",
] as const;
export type Currency = (typeof CURRENCIES)[number];

export const CURRENCY_LABELS: Record<Currency, string> = {
  USD: "US dollar",
  CAD: "Canadian dollar",
  EUR: "Euro",
  GBP: "British pound",
  AUD: "Australian dollar",
  NZD: "New Zealand dollar",
  CHF: "Swiss franc",
  SEK: "Swedish krona",
  NOK: "Norwegian krone",
  DKK: "Danish krone",
  MXN: "Mexican peso",
  INR: "Indian rupee",
  ZAR: "South African rand",
  SGD: "Singapore dollar",
  HKD: "Hong Kong dollar",
};

/** What every inventory was before it could choose, and what a new one starts as. */
export const DEFAULT_CURRENCY: Currency = "USD";

export const isCurrency = (value: unknown): value is Currency =>
  typeof value === "string" && (CURRENCIES as readonly string[]).includes(value);

export const normaliseCurrency = (value: unknown): Currency => (isCurrency(value) ? value : DEFAULT_CURRENCY);

// en-US on purpose: a fixed locale writes an amount the same on every device
// and in every test, where the browser's own would write "US$" in half the
// world. With an inventory's own currency it still writes a symbol the owner
// reads: "€1,234.50", "£1,234.50", "CA$1,234.50".
const moneyFormats = new Map<Currency, Intl.NumberFormat>();
const moneyFormat = (currency: Currency): Intl.NumberFormat => {
  let format = moneyFormats.get(currency);
  if (!format) {
    format = new Intl.NumberFormat("en-US", { style: "currency", currency });
    moneyFormats.set(currency, format);
  }
  return format;
};

/**
 * The active inventory's currency, which every amount on screen is written
 * in. The app shell sets it from the session once it knows which inventory is
 * being viewed, so the dozens of places that show an amount need not each be
 * handed it. A module setting rather than a React context because
 * `formatMoney` is also called outside components (toasts, sentences).
 */
let displayCurrency: Currency = DEFAULT_CURRENCY;
export const setDisplayCurrency = (currency: unknown): void => {
  displayCurrency = normaliseCurrency(currency);
};
export const getDisplayCurrency = (): Currency => displayCurrency;

/** An amount on screen: "$1,234.50", and "-$3.00" for a credit. Null is "". */
export const formatMoney = (cents: number | null, currency: Currency = displayCurrency): string =>
  cents === null ? "" : moneyFormat(currency).format(cents / 100);

const countFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });

/** A count on screen, with its thousands grouped: "12,480". */
export const formatCount = (value: number): string => countFormat.format(value);

/* -------------------------------------------------------- find (Stage 2) -- */

const oneOf = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T =>
  typeof value === "string" && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;

export const normaliseStatus = (value: unknown): ItemStatus => oneOf(ITEM_STATUSES, value, "keep");
export const normaliseCondition = (value: unknown): ItemCondition => oneOf(ITEM_CONDITIONS, value, "");
export const normaliseFieldType = (value: unknown): FieldType => oneOf(FIELD_TYPES, value, "text");
export const normaliseVisibility = (value: unknown): FieldVisibility =>
  oneOf<FieldVisibility>(["public", "private"], value, "public");
export const normaliseSort = (value: unknown): ItemSort | undefined =>
  typeof value === "string" && (ITEM_SORTS as readonly string[]).includes(value)
    ? (value as ItemSort)
    : undefined;

/** A whole number from 1 to `MAX_QUANTITY`; anything else is one, because one is what an item is. */
export function normaliseQuantity(value: unknown): number {
  const count = Math.trunc(Number(value));
  return Number.isFinite(count) && count >= 1 ? Math.min(count, MAX_QUANTITY) : 1;
}

/**
 * One field as a write may carry it, or null if it has no label.
 *
 * A field with a label and no value is kept: "Serial number: (blank)" is a
 * perfectly good thing to leave for later, and it is what a template produces.
 * A field with a value and no label is not -- there is nothing to call it.
 */
export function normaliseField(value: unknown): Required<FieldInput> | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  const label = clampName(input["label"]).slice(0, MAX_FIELD_LABEL_LENGTH);
  if (!label) return null;

  return {
    label,
    value: clampText(input["value"], MAX_FIELD_VALUE_LENGTH),
    type: normaliseFieldType(input["type"]),
    group: clampName(input["group"]).slice(0, MAX_FIELD_LABEL_LENGTH),
    visibility: normaliseVisibility(input["visibility"]),
  };
}

export const normaliseFields = (value: unknown): Required<FieldInput>[] =>
  (Array.isArray(value) ? value : [])
    .slice(0, MAX_FIELDS_PER_ITEM)
    .map(normaliseField)
    .filter((field): field is Required<FieldInput> => field !== null);

/** Trimmed, collapsed, de-duplicated without regard to case, and capped. The first spelling of a tag wins. */
export function normaliseTags(value: unknown): string[] {
  const seen = new Set<string>();
  const tags: string[] = [];
  for (const raw of Array.isArray(value) ? value : []) {
    const name = clampName(raw).replace(/^#+/, "").trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    tags.push(name);
    if (tags.length >= MAX_TAGS_PER_ITEM) break;
  }
  return tags;
}
